import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

export async function downloadDailyFeeReportPDF({
  rows,
  columns,
  fromDate,
  toDate,
  paymentMode,
  collegeDetails,
  logoBase64, // pass base64 or url if available
}) {
  const doc = new jsPDF({
    orientation: "landscape",
    unit: "mm",
    format: "a4",
  });

  let startY = 10;

  // --- Header: Logo left, text centered ---
  if (collegeDetails) {
    const pageWidth = doc.internal.pageSize.getWidth();
    const margin = 15;
    const logoWidth = 28;
    const logoHeight = 28;
    const logoY = startY;
    // 1. Draw logo on left
    if (logoBase64) {
      try {
        doc.addImage(logoBase64, "PNG", margin, logoY, logoWidth, logoHeight);
      } catch (e) {}
    }
    // 2. Centered text block (college name, etc.)
    let textBlockY = logoY + 2;
    let textX = pageWidth / 2;
    // College Name
    doc.setFontSize(16);
    doc.setFont("helvetica", "bold");
    doc.setTextColor(0, 0, 0);
    doc.text(collegeDetails.college_name?.toUpperCase() || "COLLEGE NAME", textX, textBlockY + 3, {
      align: "center",
    });
    // Autonomous + Accreditation
    const autoAcc = `${collegeDetails.is_autonomous ? "(AUTONOMOUS" : ""}${
      collegeDetails.accreditation
        ? (collegeDetails.is_autonomous ? "; " : "(") + collegeDetails.accreditation
        : ""
    }${collegeDetails.is_autonomous || collegeDetails.accreditation ? ")" : ""}`;
    if (autoAcc && autoAcc !== "") {
      doc.setFontSize(11);
      doc.setFont("helvetica", "italic");
      doc.text(autoAcc, textX, textBlockY + 12, { align: "center" });
    }
    // Affiliation
    if (collegeDetails.affiliation) {
      doc.setFontSize(11);
      doc.setFont("helvetica", "italic");
      doc.text(collegeDetails.affiliation, textX, textBlockY + 20, { align: "center" });
    }
    // Report Title with date range beside in brackets
    let dateRange = `(From: ${fromDate || "-"} To: ${toDate || "-"})`;
    doc.setFontSize(13);
    doc.setFont("helvetica", "bold");
    const title = "Daily Fee Report ";
    const titleWidth = doc.getTextWidth(title);
    const dateRangeWidth = doc.getTextWidth(dateRange);
    const totalWidth = titleWidth + 6 + dateRangeWidth;
    const startX = textX - totalWidth / 2;
    doc.text(title, startX, textBlockY + 32, { align: "left" });
    doc.setFontSize(11);
    doc.setFont("helvetica", "normal");
    doc.text(dateRange, startX + titleWidth + 6, textBlockY + 32, { align: "left" });
    // Payment Mode (below)
    doc.setFontSize(10);
    doc.setFont("helvetica", "normal");
    let filterInfo = `Payment Mode: ${paymentMode || "All"}`;
    doc.text(filterInfo, textX, textBlockY + 39, { align: "center" });
    startY = logoY + logoHeight + 10;
  } else {
    // Fallback
    doc.setFontSize(16);
    doc.text("Daily Fee Report", 14, 10);
    doc.setFontSize(11);
    let info = `From: ${fromDate || "-"}  To: ${toDate || "-"}  Payment Mode: ${
      paymentMode || "All"
    }`;
    doc.text(info, 14, 18);
    startY = 25;
  }

  // Group rows by receipt_date
  const tableColumns = ["S. No.", "Date", "Roll No", "Fee Head Name", "Paid Amount", "Remarks"];
  const grouped = {};
  let grandTotal = 0;
  rows.forEach((row) => {
    const date = row.receipt_date || "";
    if (!grouped[date]) grouped[date] = [];
    grouped[date].push(row);
    let amtStr = (row.paid_amount ?? "").toString().replace(/[,\s]/g, "");
    const amt = parseFloat(amtStr) || 0;
    grandTotal += amt;
  });

  // Prepare tableRows with subtotals
  let tableRows = [];
  let serialNo = 1;
  Object.keys(grouped)
    .sort((a, b) => {
      // Parse DD-MM-YYYY to Date
      const [ad, am, ay] = a.split("-");
      const [bd, bm, by] = b.split("-");
      const dateA = new Date(`${ay}-${am}-${ad}`);
      const dateB = new Date(`${by}-${bm}-${bd}`);
      return dateA - dateB;
    })
    .forEach((date) => {
      let dateTotal = 0;
      grouped[date].forEach((row) => {
        tableRows.push([
          serialNo++,
          date,
          row["student_rollno"] || "",
          row["fee_head_name"] || "",
          row["paid_amount"] || "",
          formatRemarksForColumn(row["remarks"] || "", 100),
        ]);
        let amtStr = (row.paid_amount ?? "").toString().replace(/[,\s]/g, "");
        dateTotal += parseFloat(amtStr) || 0;
      });
      // Subtotal row for this date
      tableRows.push([
        "",
        {
          content: `Total for ${date}`,
          colSpan: 3,
          styles: { halign: "right", fontStyle: "bold" },
        },
        {
          content: `Rs. ${dateTotal}`,
          colSpan: 1,
          styles: { halign: "right", fontStyle: "bold" },
        },
        "",
      ]);
    });
  // Grand total row
  tableRows.push([
    "",
    {
      content: "Grand Total",
      colSpan: 3,
      styles: { halign: "right", fontStyle: "bold", fillColor: [220, 220, 220] },
    },
    {
      content: `Rs. ${grandTotal}`,
      colSpan: 1,
      styles: { halign: "right", fontStyle: "bold", fillColor: [220, 220, 220] },
    },
    "",
  ]);

  const columnStyles = {
    0: { cellWidth: 15, halign: "center" }, // S. No.
    1: { cellWidth: 25, halign: "center" }, // Date
    2: { cellWidth: 35, halign: "center" }, // Roll No
    3: { cellWidth: 80, halign: "left" }, // Fee Head Name
    4: { cellWidth: 25, halign: "right" }, // Paid Amount
    5: { cellWidth: 100, halign: "left" }, // Remarks
  };

  autoTable(doc, {
    head: [tableColumns],
    body: tableRows,
    startY: startY,
    styles: {
      fontSize: 8,
      cellPadding: 2,
      lineColor: [0, 0, 0],
      lineWidth: 0.2,
      textColor: [0, 0, 0],
      overflow: "linebreak",
      valign: "middle",
    },
    headStyles: {
      fillColor: [200, 200, 200],
      textColor: [0, 0, 0],
      fontStyle: "bold",
      fontSize: 9,
      halign: "center",
      valign: "middle",
      cellPadding: 3,
      lineWidth: 0.2,
    },
    columnStyles: columnStyles,
    theme: "grid",
    tableWidth: "auto",
    margin: { left: 14, right: 14 },
    didParseCell: function (data) {
      data.cell.styles.textColor = [0, 0, 0];
      if (data.column.index === 3) {
        data.cell.styles.fontSize = 7;
        data.cell.styles.overflow = "linebreak";
      }
      if (data.column.index === 5) {
        data.cell.styles.fontSize = 6.5;
        data.cell.styles.overflow = "linebreak";
        data.cell.styles.minCellHeight = 8;
        data.cell.styles.cellPadding = { top: 1, bottom: 1, left: 2, right: 2 };
      }
      if (data.column.index === 0 || data.column.index === 1 || data.column.index === 2) {
        data.cell.styles.halign = "center";
      }
    },
  });

  doc.save("DailyFeeReport.pdf");
}

function formatRemarksForColumn(remarks, maxWidth) {
  if (!remarks) return "";
  const remarksStr = String(remarks);
  let formattedRemarks = remarksStr.replace(/,/g, ", ");
  const ids = formattedRemarks.split(",").map((id) => id.trim());
  const charsPerLine = Math.floor(maxWidth * 1.5);
  let result = [];
  let currentLine = "";

  ids.forEach((id, index) => {
    if (!id) return;
    if ((currentLine + (currentLine ? ", " : "") + id).length <= charsPerLine) {
      if (currentLine) {
        currentLine += ", " + id;
      } else {
        currentLine = id;
      }
    } else {
      if (currentLine) result.push(currentLine);
      currentLine = id;
    }
    if (index === ids.length - 1) result.push(currentLine);
  });

  return result.join("\n");
}
